<template>
    <div class="w-full pb-28 md:pb-0">
        <!-- Yes/No question -->
        <template v-if="hasBusinessAnswer === null">
            <div class="flex flex-col items-start justify-start gap-2 w-full pb-8 md:pb-6">
                <h1 class="self-stretch text-[24px] leading-8 md:text-[30px] md:leading-9 font-semibold text-slate-800">
                    Do you own or run a business?
                </h1>
                <p class="self-stretch text-slate-500 text-base leading-6">
                    Let us know if you're eligible for business credit cards. You can setup your business(es) in My10x to stay better organized.
                </p>
            </div>
            <div class="flex flex-col items-start gap-4 w-full">
                <label
                    class="w-full flex items-center gap-3 px-4 py-3 rounded border cursor-pointer transition-all"
                    :class="hasBusinessAnswer === true ? 'border-orange bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'"
                >
                    <input type="radio" name="has_business" :value="true" v-model="hasBusinessAnswer" class="form-radio text-orange" />
                    <span class="text-slate-800 font-medium">Yes</span>
                </label>
                <label
                    class="w-full flex items-center gap-3 px-4 py-3 rounded border cursor-pointer transition-all"
                    :class="hasBusinessAnswer === false ? 'border-orange bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'"
                >
                    <input type="radio" name="has_business" :value="false" v-model="hasBusinessAnswer" class="form-radio text-orange" />
                    <span class="text-slate-800 font-medium">No</span>
                </label>
            </div>
        </template>

        <!-- Entity form when Yes is selected -->
        <template v-else-if="hasBusinessAnswer === true">
            <div class="flex flex-col items-start justify-start gap-2 w-full pb-8 md:pb-6">
                <h1 class="self-stretch text-[24px] leading-8 md:text-[30px] md:leading-9 font-semibold text-slate-800">
                    Add entity details
                </h1>
                <p class="self-stretch text-slate-500 text-base leading-6">
                    Create an entity to keep track of all related cards
                </p>
            </div>
            <div class="flex flex-col items-start gap-6 w-full">
                <div
                    v-for="(entity, index) in businessEntitiesForm"
                    :key="index"
                    class="w-full space-y-6"
                >
                    <div class="flex items-center justify-between mb-2" v-if="businessEntitiesForm.length > 1">
                        <h3 class="text-sm font-medium text-slate-800">Entity {{ index + 1 }}</h3>
                        <button
                            type="button"
                            @click="removeBusinessEntity(index)"
                            class="text-slate-500 hover:text-red-600 transition-colors"
                        >
                            <Icon icon="close" :width="20" :height="20" />
                        </button>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-slate-600 mb-1">Entity name</label>
                        <input
                            type="text"
                            v-model="entity.name"
                            placeholder="Enter entity name"
                            class="w-full px-4 py-1.5 rounded border bg-white text-slate-800 placeholder-slate-400"
                            :class="attemptBusinessSubmit && !entity.name ? 'border-red-500' : 'border-slate-200'"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-slate-600 mb-1">Entity type</label>
                        <select
                            v-model="entity.type"
                            class="w-full px-4 py-1.5 rounded border bg-white text-slate-800"
                            :class="attemptBusinessSubmit && !entity.type ? 'border-red-500' : 'border-slate-200'"
                        >
                            <option value="" disabled>Select entity type</option>
                            <option value="sole-prop">Sole Proprietorship</option>
                            <option value="partnership">Partnership</option>
                            <option value="llc">Limited Liability Company</option>
                            <option value="s-corp">S-Corp</option>
                            <option value="c-corp">C-Corp</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-slate-600 mb-2">Tax ID type</label>
                        <div class="flex gap-4 items-center">
                            <label class="flex items-center gap-2 text-slate-800 text-sm font-medium">
                                <input type="radio" :name="'tax_id_type_' + index" value="ein" v-model="entity.tax_id_type" class="form-radio text-orange" />
                                EIN
                            </label>
                            <label class="flex items-center gap-2 text-slate-800 text-sm font-medium">
                                <input type="radio" :name="'tax_id_type_' + index" value="ssn" v-model="entity.tax_id_type" class="form-radio text-orange" />
                                SSN
                            </label>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-slate-600 mb-1">
                            Annual business spend <span class="text-slate-400">(optional)</span>
                        </label>
                        <div class="relative flex items-center border border-slate-200 rounded bg-white">
                            <span class="flex items-center justify-center px-4 py-1.5 text-slate-600 border-r border-slate-200">$</span>
                            <input
                                type="text"
                                v-model="entity.annual_business_spend"
                                placeholder="Enter amount"
                                class="flex-1 px-4 py-1.5 border-0 outline-none text-slate-800 placeholder-slate-400 rounded-r"
                                @input="formatBusinessSpend(index)"
                            />
                        </div>
                        <p class="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <Icon icon="info" :width="14" :height="14" />
                            Get best cards recommendations tailored to your business
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    class="inline-flex text-sm items-center gap-2 text-slate-800 font-medium"
                    @click="addBusinessEntity"
                >
                    <Icon icon="plus" />
                    <span>Add another entity</span>
                </button>

                <div class="w-full" v-if="errors.length > 0">
                    <Errors :errors="errors" />
                </div>
            </div>
        </template>

        <QuizActionBar
            primaryLabel="Continue"
            secondaryLabel="Skip"
            :disabled="saving"
            :show="hasBusinessAnswer !== null"
            @primary="saveBusinessEntities"
            @secondary="skipBusinessEntities"
        />
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import Errors from '@/components/errors.vue';
import QuizActionBar from './QuizActionBar.vue';
import { useQuizWizard } from '@/composables/useQuizWizard';

const {
    saving, errors,
    hasBusinessAnswer, businessEntitiesForm, attemptBusinessSubmit,
    addBusinessEntity, removeBusinessEntity, formatBusinessSpend,
    saveBusinessEntities, skipBusinessEntities,
} = useQuizWizard();
</script>
